import { computed, effect, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Platform } from '@ionic/angular';
import { Purchases } from '@revenuecat/purchases-js';
import { ArmyBuilderConfig, HttpClientWithInFlightCache, SettingsService } from 'army-builder-shared';
import { ToastService } from 'army-builder-shared/src/lib/global/toast/toast';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
    public userHasAccess$ = this.settingsService.login$.pipe(
        map((l) => {
            return l.user?.roles?.includes('app_subscriber') || l.user?.roles?.includes('app_subscriber_annual');
        })
    );
    public userHasAccess = toSignal(this.userHasAccess$);

    public newCustomer$ = this.settingsService.login$.pipe(
        map((l) => {
            return Object.keys(l.user?.entitlements ?? {}).length === 0;
        })
    );

    public userRoles$ = this.settingsService.login$.pipe(
        map((l) => {
            return l.user?.roles;
        })
    );

    public freeTrial$ = this.settingsService.login$.pipe(
        map((l) => {
            return l.user?.roles?.includes('app_free_trial');
        })
    );

    public expiry$ = this.settingsService.login$.pipe(
        map((l) => {
            const annualExpiry = new Date(l.user?.entitlements?.app_sub_annual?.expires_date).getTime() || 0;
            const monthlyExpiry = new Date(l.user?.entitlements?.app_sub?.expires_date).getTime() || 0;

            if (monthlyExpiry === 0 && annualExpiry === 0) {
                return null;
            }
            return new Date(Math.max(annualExpiry, monthlyExpiry));
        })
    );

    public hasExpired$ = this.expiry$.pipe(
        map((expiry) => {
            return expiry && expiry < new Date();
        })
    );

    allSubscriptions = computed(() => {
        const getStoreFromSubscription = (subscription) => {
            const store = subscription.store;
            if (store === 'play_store') {
                return 'ANDROID';
            }
            if (store === 'app_store') {
                return 'IOS';
            }
            if (store === 'stripe') {
                return 'WEB';
            }
            if (store === 'promotional') {
                return 'PROMO';
            }
            return null;
        };

        let user = this.settingsService.login().user;
        if (!user) {
            return [];
        }
        let subscriptionKeys = Object.keys(user?.subscriptions ?? {});
        let subscriptions = subscriptionKeys
            .map((key) => {
                const sub = user?.subscriptions[key];
                const store = getStoreFromSubscription(sub);
                const expiryDate = new Date(sub.expires_date);
                const unsubscribeDate = sub.unsubscribe_detected_at ? new Date(sub.unsubscribe_detected_at) : null;
                return { store, expiryDate, unsubscribeDate };
            })
            .filter((sub) => sub.store !== null && sub.expiryDate >= new Date() && !sub.unsubscribeDate)
            .sort((a, b) => a.expiryDate.getTime() - b.expiryDate.getTime());

        return subscriptions;
    });

    currentSubscription = computed(() => {
        const subscriptions = this.allSubscriptions();
        if (subscriptions.length > 0) {
            return subscriptions[0];
        }
        return null;
    });

    subscriptionPlatform = computed(() => {
        return this.currentSubscription()?.store;
    });

    subscriptionCount = computed(() => {
        const subscriptions = this.allSubscriptions();
        return subscriptions.length;
    });

    constructor(
        private http: HttpClientWithInFlightCache,
        private settingsService: SettingsService,
        private config: ArmyBuilderConfig,
        private toast: ToastService,
        private router: Router,
        private platform: Platform
    ) {
        let billingIssueWarningShown = false;
        effect(() => {
            const user = this.settingsService.login().user;
            if (!user) return;

            const hasActiveBillingIssue = user['hasActiveBillingIssue'];
            const isCancelledDueToBillingIssue = user['isCancelledDueToBillingIssue'];

            if (!hasActiveBillingIssue && !isCancelledDueToBillingIssue) return;
            if (billingIssueWarningShown) return;
            billingIssueWarningShown = true;

            const isNative = this.platform.is('ios') || this.platform.is('android');
            const isStripeOnNative = isNative && this.subscriptionPlatform() === 'WEB';

            let message: string;
            if (isCancelledDueToBillingIssue) {
                message = isStripeOnNative
                    ? 'Your subscription was cancelled due to a billing issue. Please log into the web app to manage your subscription.'
                    : 'Your subscription was cancelled due to a billing issue.';
            } else {
                message = isStripeOnNative
                    ? "We're having trouble processing your subscription renewal. Please log into the web app to update your payment details."
                    : "We're having trouble processing your subscription renewal.";
            }

            const buttons = isStripeOnNative
                ? [{ text: 'Close', role: 'cancel' as const }]
                : [
                      { text: 'Close', role: 'cancel' as const },
                      { text: 'Manage Subscription', role: 'ok' as const }
                  ];

            this.toast.showToast('Billing Issue', message, { color: 'danger', buttons, duration: 0 }).then((res) => {
                if (res.role === 'ok') {
                    this.router.navigateByUrl('/settings/subscription');
                }
            });
        });

        let subscriptionWarningShown = false;
        effect(() => {
            let subscriptionCount = this.subscriptionCount();
            let allSubscriptions = this.allSubscriptions();
            let platforms = allSubscriptions.map((sub) => sub.store);
            let uniquePlatforms = [...new Set(platforms)];
            let uniquePlatformsDescription = uniquePlatforms.join(' and ');
            if (uniquePlatforms.length > 2) {
                uniquePlatformsDescription = uniquePlatforms.slice(0, -1).join(', ') + ' and ' + uniquePlatforms.slice(-1);
            }

            const msg = `You have ${subscriptionCount} active subscription(s) on ${uniquePlatformsDescription}.`;
            if (subscriptionCount > 1) {
                if (subscriptionWarningShown) {
                    return;
                }
                subscriptionWarningShown = true;
                this.toast
                    .showToast('Multiple subscriptions detected', msg, {
                        color: 'warning',
                        buttons: [
                            {
                                text: 'Close',
                                role: 'cancel'
                            },
                            {
                                text: 'Learn more',
                                role: 'ok'
                            }
                        ]
                    })
                    .then((res) => {
                        console.log('Toast dismissed', res);
                        if (res.role === 'ok') {
                            this.router.navigateByUrl('/pages/multiple-subscriptions');
                        }
                    });
            }
        });
    }

    async checkSubscriptionStatus() {
        try {
            console.log('Checking subscription status');
            const customerInfo = await Purchases.getSharedInstance().getCustomerInfo();
            console.log({ customerInfo });
            // access latest customerInfo
        } catch (e) {
            // Handle errors fetching customer info
        }
    }
}
